import React, { useState } from 'react';
import { Plus, Eye, GitCompare, Copy, Lock, Unlock, Check, Trash2, Loader2, Pencil } from 'lucide-react';
import toast from 'react-hot-toast';
import type { ForecastVersion } from '../../types';
import { StatusPill } from '../shared/StatusPill';
import { TableSkeleton } from '../shared/Skeleton';
import { EmptyState } from '../shared/EmptyState';
import { CreateVersionModal, type CreateVersionParams } from './CreateVersionModal';
import { EditVersionModal, type EditVersionParams } from './EditVersionModal';
import { useVersions } from '../../hooks/useVersions';
import { useForecastV3 } from '../../context/ForecastV3Context';
import { defaultMonthForYear } from '../../utils';

interface VersionsPageProps {
  versions: ForecastVersion[];
}

export function VersionsPage({
  versions: initialVersions,
}: VersionsPageProps) {
  const { state, setVersion, setActiveTab, setYear, setActiveMonth } = useForecastV3();
  const { orgId, locationId, year, currentVersion } = state;

  // Use the versions hook for real data and mutations
  const {
    versions,
    isLoading,
    createVersion,
    lockVersion,
    unlockVersion,
    publishVersion,
    deleteVersion,
    copyVersion,
    updateVersion,
  } = useVersions({
    orgId: orgId ?? 0,
    locationId: locationId ?? 0,
    enabled: !!orgId && !!locationId,
  });

  // Use hook versions if available, otherwise fall back to props
  const displayVersions = versions.length > 0 ? versions : initialVersions;

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingVersion, setEditingVersion] = useState<ForecastVersion | null>(null);
  const [actionInProgress, setActionInProgress] = useState<{ id: number | string; action: string } | null>(null);

  // Action handlers
  const handleCreateVersion = async (params: CreateVersionParams) => {
    const created = await createVersion({
      name: params.name,
      type: params.type,
      baseVersionId: params.baseVersionId ? String(params.baseVersionId) : undefined,
      copyOptions: params.copyOptions,
      year: params.year,
    });
    // Make the new version the working version so it's immediately editable.
    // The header version dropdown is filtered by the selected year, so a version
    // created for another year would otherwise be invisible until the user
    // manually switches years.
    if (created.year && created.year !== year) {
      setYear(created.year);
      setActiveMonth(defaultMonthForYear(created.year));
      toast(`Switched to ${created.year} to open "${created.name}"`, { icon: '📅' });
    }
    setVersion(created);
    return created;
  };

  const handleViewVersion = (version: ForecastVersion) => {
    // Keep the workspace year in sync with the version being opened — the
    // header dropdown only lists versions for the selected year.
    if (version.year && version.year !== year) {
      setYear(version.year);
      setActiveMonth(defaultMonthForYear(version.year));
      toast(`Switched to ${version.year} to open "${version.name}"`, { icon: '📅' });
    }
    setVersion(version);
    setActiveTab('workspace');
  };

  const handleCompareVersion = (version: ForecastVersion) => {
    setActiveTab('compare');
  };

  const handleLockVersion = async (version: ForecastVersion) => {
    setActionInProgress({ id: version.id, action: 'lock' });
    try {
      await lockVersion(String(version.id));
      toast.success(`"${version.name}" has been locked`);
    } catch (error) {
      toast.error('Failed to lock version');
    } finally {
      setActionInProgress(null);
    }
  };

  const handleUnlockVersion = async (version: ForecastVersion) => {
    setActionInProgress({ id: version.id, action: 'unlock' });
    try {
      await unlockVersion(String(version.id));
      toast.success(`"${version.name}" has been unlocked`);
    } catch (error) {
      toast.error('Failed to unlock version');
    } finally {
      setActionInProgress(null);
    }
  };

  const handlePublishVersion = async (version: ForecastVersion) => {
    const ok = window.confirm(version.type === 'Budget'
      ? `Publish "${version.name}" as the official budget?\n\n` +
        `This writes the version's monthly values to the budget table for the months it covers ` +
        `(replacing any existing budget for those months at this property) so dashboards, reports and ` +
        `budget-vs-actual comparisons pick it up. Position-level labor drivers are pushed to the labor budget.`
      : `Publish "${version.name}" as the official forecast?\n\n` +
        `This writes the version's monthly values to the forecast table for the months it covers, ` +
        `replacing the previously published forecast for those months at this property.`);
    if (!ok) return;
    setActionInProgress({ id: version.id, action: 'publish' });
    try {
      await publishVersion(String(version.id));
      toast.success(version.type === 'Budget'
        ? `"${version.name}" published — budget and labor budget updated`
        : `"${version.name}" published — official forecast updated`);
    } catch (error) {
      toast.error('Failed to publish version');
    } finally {
      setActionInProgress(null);
    }
  };

  const handleDuplicateVersion = async (version: ForecastVersion) => {
    setActionInProgress({ id: version.id, action: 'duplicate' });
    try {
      const newName = `${version.name} (Copy)`;
      await copyVersion(String(version.id), newName);
      toast.success(`Created copy of "${version.name}"`);
    } catch (error) {
      toast.error('Failed to duplicate version');
    } finally {
      setActionInProgress(null);
    }
  };

  const handleEditVersion = async (params: EditVersionParams) => {
    if (!editingVersion) return;
    await updateVersion(String(editingVersion.id), params);
    const label = params.name ?? editingVersion.name;
    if (params.year && params.year !== editingVersion.year) {
      toast.success(`"${label}" moved to fiscal year ${params.year}`);
      // Keep the open workspace in step: the header dropdown only lists the selected year.
      if (currentVersion?.id === editingVersion.id) {
        setVersion({ ...currentVersion, name: label, year: params.year, coverage: `Jan-Dec ${params.year}` });
        setYear(params.year);
        setActiveMonth(defaultMonthForYear(params.year));
      }
    } else {
      toast.success(`"${label}" updated`);
      if (currentVersion?.id === editingVersion.id) {
        setVersion({ ...currentVersion, name: label, description: params.description ?? currentVersion.description });
      }
    }
  };

  const handleDeleteVersion = async (version: ForecastVersion) => {
    if (!window.confirm(`Are you sure you want to delete "${version.name}"? This action cannot be undone.`)) {
      return;
    }
    setActionInProgress({ id: version.id, action: 'delete' });
    try {
      await deleteVersion(String(version.id));
      toast.success(`"${version.name}" has been deleted`);
    } catch (error) {
      toast.error('Failed to delete version');
    } finally {
      setActionInProgress(null);
    }
  };

  const isActionInProgress = (versionId: number) => actionInProgress?.id === versionId;

  return (
    <div className="flex-1 p-6 overflow-auto">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold" style={{ color: 'var(--lodge-onyx-800)', fontFamily: 'var(--font-sans)', letterSpacing: '-0.3px' }}>Forecast Versions</h2>
            <p className="text-sm mt-1" style={{ color: 'var(--lodge-onyx-500)' }}>
              Manage, compare, and publish your forecast versions
            </p>
          </div>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 font-semibold text-sm rounded-lg transition-all"
            style={{ background: 'var(--lodge-orange-400)', color: '#ffffff', border: 'none', boxShadow: 'var(--shadow-orange)' }}
            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--lodge-orange-500)'; (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(-1px)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--lodge-orange-400)'; (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(0)'; }}
          >
            <Plus size={16} />
            Create Version
          </button>
        </div>

        {/* Versions Table */}
        <div className="rounded-xl overflow-hidden" style={{ background: 'var(--lodge-white)', border: '1px solid var(--lodge-onyx-100)', boxShadow: 'var(--shadow-sm)' }}>
          <table className="w-full text-sm">
            <thead>
              <tr style={{ background: 'var(--lodge-onyx-50)', borderBottom: '1px solid var(--lodge-onyx-100)' }}>
                <th className="text-left py-3 px-4 font-bold uppercase" style={{ fontSize: 11, letterSpacing: '0.8px', color: 'var(--lodge-onyx-500)' }}>Version Name</th>
                <th className="text-left py-3 px-4 font-bold uppercase" style={{ fontSize: 11, letterSpacing: '0.8px', color: 'var(--lodge-onyx-500)' }}>Type</th>
                <th className="text-left py-3 px-4 font-bold uppercase" style={{ fontSize: 11, letterSpacing: '0.8px', color: 'var(--lodge-onyx-500)' }}>Status</th>
                <th className="text-left py-3 px-4 font-bold uppercase" style={{ fontSize: 11, letterSpacing: '0.8px', color: 'var(--lodge-onyx-500)' }}>Created From</th>
                <th className="text-left py-3 px-4 font-bold uppercase" style={{ fontSize: 11, letterSpacing: '0.8px', color: 'var(--lodge-onyx-500)' }}>Owner</th>
                <th className="text-left py-3 px-4 font-bold uppercase" style={{ fontSize: 11, letterSpacing: '0.8px', color: 'var(--lodge-onyx-500)' }}>Coverage</th>
                <th className="text-right py-3 px-4 font-bold uppercase" style={{ fontSize: 11, letterSpacing: '0.8px', color: 'var(--lodge-onyx-500)' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {displayVersions.map((version) => (
                <tr
                  key={version.id}
                  className="transition-colors"
                  style={{ borderBottom: '1px solid var(--lodge-onyx-50)' }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'var(--lodge-orange-50)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                >
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <span className="font-medium" style={{ color: 'var(--lodge-onyx-800)' }}>{version.name}</span>
                      {currentVersion?.id === version.id && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded font-semibold" style={{ background: 'var(--lodge-orange-100)', color: 'var(--lodge-orange-600)' }}>
                          CURRENT
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <span
                      className="text-xs px-2 py-1 rounded-full font-medium"
                      style={version.type === 'Budget'
                        ? { background: 'var(--color-info-bg)', color: 'var(--color-info-ink)' }
                        : { background: 'var(--lodge-onyx-100)', color: 'var(--lodge-onyx-600)' }}
                    >
                      {version.type}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <StatusPill status={version.status} />
                  </td>
                  <td className="py-3 px-4" style={{ color: 'var(--lodge-onyx-600)' }}>{version.createdFrom || '—'}</td>
                  <td className="py-3 px-4" style={{ color: 'var(--lodge-onyx-600)' }}>{version.owner}</td>
                  <td className="py-3 px-4" style={{ color: 'var(--lodge-onyx-600)' }}>{version.coverage}</td>
                  <td className="py-3 px-4">
                    <div className="flex items-center justify-end gap-1">
                      {isActionInProgress(version.id) ? (
                        <Loader2 size={16} className="animate-spin" style={{ color: 'var(--lodge-orange-400)' }} />
                      ) : (
                        <>
                          <button
                            onClick={() => handleViewVersion(version)}
                            className="p-1.5 rounded transition-colors"
                            style={{ color: 'var(--lodge-onyx-400)' }}
                            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--lodge-onyx-700)'; (e.currentTarget as HTMLButtonElement).style.background = 'var(--lodge-onyx-50)'; }}
                            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--lodge-onyx-400)'; (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
                            title="View"
                          >
                            <Eye size={16} />
                          </button>
                          <button
                            onClick={() => handleCompareVersion(version)}
                            className="p-1.5 rounded transition-colors"
                            style={{ color: 'var(--lodge-onyx-400)' }}
                            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--lodge-onyx-700)'; (e.currentTarget as HTMLButtonElement).style.background = 'var(--lodge-onyx-50)'; }}
                            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--lodge-onyx-400)'; (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
                            title="Compare"
                          >
                            <GitCompare size={16} />
                          </button>
                          <button
                            onClick={() => handleDuplicateVersion(version)}
                            className="p-1.5 rounded transition-colors"
                            style={{ color: 'var(--lodge-onyx-400)' }}
                            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--lodge-onyx-700)'; (e.currentTarget as HTMLButtonElement).style.background = 'var(--lodge-onyx-50)'; }}
                            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--lodge-onyx-400)'; (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
                            title="Duplicate"
                          >
                            <Copy size={16} />
                          </button>
                          {version.status === 'Draft' && (
                            <>
                              <button
                                onClick={() => setEditingVersion(version)}
                                className="p-1.5 rounded transition-colors"
                                style={{ color: 'var(--lodge-onyx-400)' }}
                                onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--lodge-onyx-700)'; (e.currentTarget as HTMLButtonElement).style.background = 'var(--lodge-onyx-50)'; }}
                                onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--lodge-onyx-400)'; (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
                                title="Edit name, description or fiscal year"
                              >
                                <Pencil size={16} />
                              </button>
                              <button
                                onClick={() => handleLockVersion(version)}
                                className="p-1.5 rounded transition-colors"
                                style={{ color: 'var(--lodge-onyx-400)' }}
                                onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--color-success)'; (e.currentTarget as HTMLButtonElement).style.background = 'var(--color-success-bg)'; }}
                                onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--lodge-onyx-400)'; (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
                                title="Lock"
                              >
                                <Lock size={16} />
                              </button>
                              <button
                                onClick={() => handlePublishVersion(version)}
                                className="p-1.5 rounded transition-colors"
                                style={{ color: 'var(--color-success)' }}
                                onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--color-success-bg)'; }}
                                onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
                                title={version.type === 'Budget' ? 'Publish — sets the official budget' : 'Publish'}
                              >
                                <Check size={16} />
                              </button>
                            </>
                          )}
                          {version.status === 'Locked' && (
                            <>
                              <button
                                onClick={() => handleUnlockVersion(version)}
                                className="p-1.5 rounded transition-colors"
                                style={{ color: 'var(--lodge-onyx-400)' }}
                                onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--color-warning)'; (e.currentTarget as HTMLButtonElement).style.background = 'var(--color-warning-bg)'; }}
                                onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--lodge-onyx-400)'; (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
                                title="Unlock"
                              >
                                <Unlock size={16} />
                              </button>
                              <button
                                onClick={() => handlePublishVersion(version)}
                                className="p-1.5 rounded transition-colors"
                                style={{ color: 'var(--color-success)' }}
                                onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--color-success-bg)'; }}
                                onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
                                title="Publish"
                              >
                                <Check size={16} />
                              </button>
                            </>
                          )}
                          {version.status === 'Draft' && (
                            <button
                              onClick={() => handleDeleteVersion(version)}
                              className="p-1.5 rounded transition-colors"
                              style={{ color: 'var(--lodge-onyx-400)' }}
                              onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--color-error)'; (e.currentTarget as HTMLButtonElement).style.background = 'var(--color-error-bg)'; }}
                              onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--lodge-onyx-400)'; (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
                              title="Delete"
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Loading state */}
        {isLoading && displayVersions.length === 0 && (
          <div className="rounded-xl overflow-hidden" style={{ background: 'var(--lodge-white)', border: '1px solid var(--lodge-onyx-100)' }}>
            <TableSkeleton rows={4} />
          </div>
        )}

        {/* Empty state */}
        {!isLoading && displayVersions.length === 0 && (
          <div className="rounded-xl" style={{ background: 'var(--lodge-white)', border: '1px solid var(--lodge-onyx-100)' }}>
            <EmptyState
              type="versions"
              action={{
                label: 'Create your first version',
                onClick: () => setIsCreateModalOpen(true),
              }}
            />
          </div>
        )}
      </div>

      <EditVersionModal
        isOpen={!!editingVersion}
        version={editingVersion}
        onClose={() => setEditingVersion(null)}
        onSubmit={handleEditVersion}
      />

      {/* Create Version Modal */}
      <CreateVersionModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreateVersion}
        existingVersions={displayVersions}
        currentYear={year}
      />
    </div>
  );
}

export default VersionsPage;
